import { memo, useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Search, X, ChevronDown } from 'lucide-react';

const MAX_RENDERED = 100;

function CustomMultiSelectBase({
  options = [],
  value = [],
  onChange,
  placeholder = 'Select',
  className = '',
  disabled = false,
  showSelectAll = false,
  selectAllLabel = 'Select All',
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const inputRef = useRef(null);
  const selectAllRef = useRef(null);

  const normalized = useMemo(
    () =>
      options
        .map((opt) => {
          if (typeof opt === 'string') {
            return { label: opt, value: opt };
          }

          const label = opt?.label ?? opt?.value ?? '';
          const optionValue = opt?.value ?? opt?.label ?? '';

          return {
            label: String(label),
            value: String(optionValue),
          };
        })
        .filter((opt) => opt.label && opt.value),
    [options],
  );

  const selectedLabels = useMemo(
    () => normalized.filter((o) => value.includes(o.value)).map((o) => o.label),
    [normalized, value],
  );

  const allSelected = normalized.length > 0 && value.length === normalized.length;
  const someSelected = value.length > 0 && !allSelected;

  const filtered = useMemo(() => {
    if (!query.trim()) return normalized.slice(0, MAX_RENDERED);
    const q = query.toLowerCase();
    const results = [];

    for (const opt of normalized) {
      if (opt.label && opt.label.toLowerCase().includes(q)) {
        results.push(opt);
        if (results.length >= MAX_RENDERED) break;
      }
    }

    return results;
  }, [normalized, query]);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 30);
      setQuery('');
    }
  }, [open]);

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someSelected;
    }
  }, [someSelected]);

  const handleToggle = useCallback(
    (selectedValue) => {
      if (disabled) return;
      const updated = value.includes(selectedValue)
        ? value.filter((v) => v !== selectedValue)
        : [...value, selectedValue];
      onChange(updated);
    },
    [disabled, value, onChange],
  );

  const handleClear = useCallback(
    (e) => {
      e.stopPropagation();
      if (disabled) return;
      onChange([]);
    },
    [disabled, onChange],
  );

  const handleSelectAll = useCallback(() => {
    if (disabled) return;
    onChange(allSelected ? [] : normalized.map((opt) => opt.value));
  }, [allSelected, disabled, normalized, onChange]);

  const displayText =
    selectedLabels.length === 0
      ? placeholder
      : selectedLabels.length === 1
        ? selectedLabels[0]
        : `${selectedLabels.length} selected`;

  return (
    <Popover open={open} onOpenChange={(next) => !disabled && setOpen(next)}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={`w-full h-[42px] px-4 flex items-center justify-between text-[#000000]
            bg-[#ffffff] border rounded-md truncate focus:bg-[#000000]/10
            focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed ${className}`}
        >
          <span
            className={`truncate text-sm ${selectedLabels.length === 0 ? 'text-[#000000]/70' : ''}`}
          >
            {displayText}
          </span>
          <div className="flex items-center gap-1 shrink-0 ml-2">
            {selectedLabels.length > 0 && (
              <X
                className="h-3.5 w-3.5 opacity-50 hover:opacity-100"
                onClick={handleClear}
              />
            )}
            <ChevronDown className="h-4 w-4 opacity-50" />
          </div>
        </button>
      </PopoverTrigger>

      <PopoverContent
        className="p-0 rounded-md border bg-[#ffffff] shadow-lg"
        style={{ width: 'var(--radix-popover-trigger-width)' }}
        align="start"
        sideOffset={4}
      >
        <div className="flex items-center px-3 py-2 border-b bg-white rounded-t-md">
          <Search className="h-4 w-4 text-[#000000]/50 shrink-0 mr-2" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={`Search ${placeholder.replace('Select ', '')}...`}
            className="flex-1 text-sm text-[#000000] bg-transparent outline-none placeholder:text-[#000000]/40"
          />
          {query && (
            <X
              className="h-3.5 w-3.5 text-[#000000]/40 cursor-pointer hover:text-[#000000]"
              onClick={() => setQuery('')}
            />
          )}
        </div>

        <div className="overflow-y-auto" style={{ maxHeight: 288 }}>
          {showSelectAll && normalized.length > 0 && (
            <label className="flex items-center gap-2 w-full px-3 py-2 text-sm cursor-pointer text-[#000000]
                  hover:bg-[#000000]/15 transition-colors border-b border-gray-100">
              <input
                ref={selectAllRef}
                type="checkbox"
                checked={allSelected}
                onChange={handleSelectAll}
                className="h-4 w-4 accent-[#005787] cursor-pointer"
                disabled={disabled}
              />
              <span className="truncate font-medium">{selectAllLabel}</span>
            </label>
          )}
          {filtered.length === 0 ? (
            <p className="text-sm text-[#000000]/50 text-center py-4">
              No results found
            </p>
          ) : (
            filtered.map((opt) => (
              <label
                key={opt.value}
                className="flex items-center gap-2 w-full px-3 py-2 text-sm cursor-pointer text-[#000000]
                  hover:bg-[#000000]/15 transition-colors"
              >
                <input
                  type="checkbox"
                  checked={value.includes(opt.value)}
                  onChange={() => handleToggle(opt.value)}
                  className="h-4 w-4 accent-[#005787] cursor-pointer"
                  disabled={disabled}
                />
                <span className="truncate">{opt.label}</span>
              </label>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export const CustomMultiSelect = memo(CustomMultiSelectBase);
